import { useEffect } from 'react';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import { CharacterCount } from './components/CharacterCount';
import { EditorContent, useEditor } from '@tiptap/react';
import { SubmitErrorComponent } from './components/SubmitError';
import type { EditorPreset } from './presets';
import { useEditorContext, EditorContext } from './editor_context';
import { setEditable, setEditor } from './editor_cache';
import {
   parseEditorContent,
   getDefaultExtensions,
   getExtensionsForPreset,
   MenuBar,
} from './presets';
import type { CharacterCountConfig } from './extensions/custom_character_count';

const TiptapEditorComponent = (props: {
   editorid: string;
   preset: EditorPreset;
   characterCountConfig?: boolean;
   disabled?: boolean;
   onEditorReady?: () => void;
}) => {
   const { editorid, characterCountConfig, preset, disabled, onEditorReady } = props;
   const { editor } = useEditorContext();
   const isFocused = editor?.storage.isFocused.isFocused;
   const submitError = editor?.storage.submitError.error;

   if (disabled) {
      setEditable(editorid, false);
   }

   useEffect(() => {
      if (editor && onEditorReady) {
         onEditorReady();
      }
   }, [editor, onEditorReady]);

   return (
      <>
         <div id={editorid} className="ProseMirror-menubar-wrapper">
            {!disabled && <MenuBar preset={preset} />}
            <EditorContent editor={editor} />
         </div>
         {isFocused && characterCountConfig ? <CharacterCount /> : null}
         {submitError ? <SubmitErrorComponent error={submitError} /> : null}
         {disabled && (
            <style>
               {`
               .ProseMirror-menubar-wrapper .ProseMirror {
                  background-color: #d3d7d9;
                  border: 1px solid rgba(0, 3, 6, 0.12);
                  cursor: not-allowed;
                  margin-top: 15px;
                  opacity: 0.6;
                  pointer-events: none;
               }
            `}
            </style>
         )}
      </>
   );
};

const withEditorContext = (Component: any) =>
   function withEditorContextComponent(props: {
      editorid: string;
      text?: string;
      preset: EditorPreset;
      characterCountConfig?: CharacterCountConfig;
      submitHandler?: () => void;
      handleModified?: (modified: boolean) => void;
   }) {
      const { editorid, text = '', preset } = props;

      const extensions = [...getDefaultExtensions(props), ...getExtensionsForPreset(preset)];

      // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useEditor" is called in "withEditorContext" that is neither a React function component nor a custom Hook ...
      const editor = useEditor({
         extensions,
         content: parseEditorContent(text),
      });

      const editorContext = {
         editor,
         preset,
      };

      // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useEffect" is called in "withEditorContext" that is neither a React function component nor a custom Hook ...
      useEffect(() => {
         if (editor) {
            setEditor(editorid, editor);
         }
      }, [editor]);

      return (
         // @ts-expect-error TS(2322) FIXME: Type '{ editor: Editor | null; }' is not assignabl... Remove this comment to see the full error message
         <EditorContext.Provider value={editorContext}>
            <Component {...props} />
         </EditorContext.Provider>
      );
   };

export const TiptapEditor = withEditorContext(TiptapEditorComponent);

initializeChakraComponent('TiptapEditor', TiptapEditor);
